import * as amplitude from '@amplitude/analytics-browser';

export default class AnalyticsClient {
  // Amplitude is disabled while quota is exceeded (CX-4920). Every method that
  // touches the SDK gates on this first, so the disabled path makes zero SDK
  // calls — no events sent, and no setUserId/reset callbacks queued against the
  // uninitialized client (initAmplitude() never runs, so such callbacks would
  // buffer in memory forever). Flip REACT_APP_AMPLITUDE_ENABLED to re-enable.
  private isEnabled() {
    return process.env.REACT_APP_AMPLITUDE_ENABLED === 'true';
  }

  private track(event: string, properties: Record<string, any>) {
    if (!this.isEnabled()) return;
    amplitude.track(event, properties);
  }

  // Set the Amplitude user ID to the Mira profile ID so FE events (e.g.
  // user_login) link to backend events, which are sent via Segment keyed by
  // the same profile ID. Without this, FE events stay on an anonymous device
  // ID and the onboarding funnel can't join FE and BE events.
  identifyUser(userId: string) {
    if (!this.isEnabled()) return;
    amplitude.setUserId(userId);
  }

  // Clear the Amplitude user ID on logout so the next session/user is not
  // attributed to the previous profile.
  resetUser() {
    if (!this.isEnabled()) return;
    amplitude.reset();
  }

  trackUserLogin(p: { firstLogin: boolean }) {
    this.track('user_login', { first_login: p.firstLogin });
  }

  trackActivationCodeSubmitted(p: { pageName: string }) {
    this.track('activation_code_submitted', { page_name: p.pageName });
  }

  trackRegisterDeviceRequest(p: { activationCode: string }) {
    this.track('device_register_request', {
      activation_code: p.activationCode,
    });
  }

  trackRegisterDeviceFailure(p: { activationCode: string }) {
    this.track('device_register_failure', {
      activation_code: p.activationCode,
    });
  }

  trackCreatePresentationSuccess(p: {
    presentationId: string;
    applicationId: string;
    appVersionId: string;
  }) {
    this.track('presentation_create_success', {
      presentation_id: p.presentationId,
      application_id: p.applicationId,
      application_version_id: p.appVersionId,
    });
  }

  trackUpdatePresentationSuccess(p: {
    presentationId: string;
    applicationId: string;
    appVersionId: string;
  }) {
    this.track('presentation_update_success', {
      presentation_id: p.presentationId,
      application_id: p.applicationId,
      application_version_id: p.appVersionId,
    });
  }

  trackPlaylistDuplicateClicked(p: { playlistId: string }) {
    this.track('playlist_duplicate_clicked', { playlist_id: p.playlistId });
  }

  trackPlaylistRulesOpened(p: { playlistId: string }) {
    this.track('playlist_rules_opened', { playlist_id: p.playlistId });
  }

  trackPlaylistTagsOpened(p: { playlistId: string }) {
    this.track('playlist_tags_opened', { playlist_id: p.playlistId });
  }

  trackPlaylistScheduleOpened(p: { playlistId: string }) {
    this.track('playlist_schedule_opened', { playlist_id: p.playlistId });
  }

  trackPlaylistScheduleSaved(p: { playlistId: string }) {
    this.track('playlist_schedule_saved', { playlist_id: p.playlistId });
  }

  trackPlaylistScheduleClicked(p: {
    playlistId: string;
    scheduleAction: string;
    scheduleValue: string | boolean;
  }) {
    this.track('playlist_schedule_clicked', {
      playlist_id: p.playlistId,
      schedule_action: p.scheduleAction,
      schedule_value: p.scheduleValue,
    });
  }

  trackSelectPlaylist(p: {
    playlistId: string;
    applicationId?: string;
    presentationId?: string;
    deviceId?: string;
  }) {
    this.track('playlist_select_success', {
      playlist_id: p.playlistId,
      application_id: p.applicationId,
      presentation_id: p.presentationId,
      device_id: p.deviceId,
    });
  }

  trackPageView(p: { page: string }) {
    this.track('page_viewed', p);
  }

  trackGetPlanCTAClicked(p: { page: string; buttonLocation: string }) {
    this.track('get_plan_cta_clicked', {
      page: p.page,
      button_location: p.buttonLocation,
    });
  }

  trackQuickStartGuideItemClicked(p: { buttonName: string }) {
    this.track('quickstart_cta_clicked', { button_name: p.buttonName });
  }

  trackGoToLibraryCTAClicked(p: { page: string; buttonLocation: string }) {
    this.track('goto_library_page_cta_clicked', {
      page: p.page,
      button_location: p.buttonLocation,
    });
  }

  trackPlaylistBuilderCTAClicked(p: { page: string; buttonLocation: string }) {
    this.track('playlist_builder_cta_clicked', {
      page: p.page,
      button_location: p.buttonLocation,
    });
  }

  trackScreensPageCTAClicked(p: { page: string; buttonLocation: string }) {
    this.track('screens_page_cta_clicked', {
      page: p.page,
      button_location: p.buttonLocation,
    });
  }

  trackBillingCycleChanged(p: { plan: string; billingCycle: string }) {
    this.track('plan_billing_cycle_toggled', {
      plan: p.plan,
      billing_cycle: p.billingCycle,
    });
  }

  trackQuantityChanged(p: {
    plan: string;
    quantity: number;
    billingCycle: string;
  }) {
    this.track('plan_quantity_changed', {
      plan: p.plan,
      quantity: p.quantity,
      billing_cycle: p.billingCycle,
    });
  }

  trackBulkOrderPopupShown(p: {
    plan: string;
    quantity: number;
    popupVariant: string;
  }) {
    this.track('bulk_order_popup_shown', {
      plan: p.plan,
      quantity: p.quantity,
      popup_variant: p.popupVariant,
    });
  }

  trackBulkOrderCTAClicked(p: {
    plan: string;
    quantity: number;
    popupVariant: string;
  }) {
    this.track('bulk_order_cta_clicked', {
      plan: p.plan,
      quantity: p.quantity,
      popup_variant: p.popupVariant,
    });
  }

  trackReviewOrderClicked(p: {
    plan: string;
    quantity: number;
    billingCycle: string;
  }) {
    this.track('review_order_clicked', {
      plan: p.plan,
      quantity: p.quantity,
      billing_cycle: p.billingCycle,
    });
  }

  trackStripeCheckoutLoaded(p: {
    plan: string;
    quantity: number;
    billingCycle: string;
  }) {
    this.track('stripe_checkout_loaded', {
      plan: p.plan,
      quantity: p.quantity,
      billing_cycle: p.billingCycle,
    });
  }

  trackStripeCheckoutAbandoned(p: {}) {
    this.track('stripe_checkout_abandoned', p);
  }

  trackStripeCheckoutSuccessPageLoaded(p: {}) {
    this.track('checkout_success_page_loaded', p);
  }

  trackStripeCheckoutErrorPageLoaded(p: {}) {
    this.track('checkout_error_page_loaded', p);
  }

  trackInvoiceCTAClicked(p: {}) {
    this.track('invoice_cta_clicked', p);
  }

  trackBillingAccountsDropdownClicked(p: { page: string }) {
    this.track('billing_accounts_dropdown_clicked', p);
  }

  trackManageSubscriptionClicked(p: {
    page: string;
    billingAccountId: string;
    billingAccountName: string;
  }) {
    this.track('manage_subscription_clicked', {
      page: p.page,
      billing_account_id: p.billingAccountId,
      billing_account_name: p.billingAccountName,
    });
  }

  trackProfileMenuItemClicked(p: { menuItem: string }) {
    this.track('profile_menu_item_clicked', { menu_item: p.menuItem });
  }

  trackAddScreenModalShown(p: { pageName: string }) {
    this.track('add_screen_modal_shown', { page_name: p.pageName });
  }

  trackUsersPageOpened(p: { pageName: string }) {
    this.track('users_page_opened', { page_name: p.pageName });
  }

  trackAddScreen(p: { pageName: string }) {
    this.track('add_screen', { page_name: p.pageName });
  }

  trackAddLocation(p: { pageName: string }) {
    this.track('add_location', { page_name: p.pageName });
  }

  trackLibraryAddButtonClicked(p: { pageName: string; entryPoint: string }) {
    this.track('library_add_button_clicked', {
      page_name: p.pageName,
      entry_point: p.entryPoint,
    });
  }

  trackLibraryItemSelected(p: {
    itemType: 'folder' | 'presentation' | 'playlist';
  }) {
    this.track('library_item_selected', { item_type: p.itemType });
  }

  trackPresentationAppSelected(p: { applicationName: string }) {
    this.track('presentation_app_selected', {
      application_name: p.applicationName,
    });
  }

  trackSharedContentViewed(p: { pageName: string }) {
    this.track('shared_content_viewed', { page_name: p.pageName });
  }

  trackPromoBannerCTAClicked(p: { promoType: string; location: string }) {
    this.track('promo_banner_cta_clicked', {
      promo_type: p.promoType,
      location: p.location,
    });
  }

  trackSchedulingBannerViewed() {
    this.track('scheduling_banner_viewed', {});
  }

  trackSchedulingBannerClicked() {
    this.track('scheduling_banner_clicked', {});
  }

  trackSchedulingBannerDismissed() {
    this.track('scheduling_banner_dismissed', {});
  }

  trackSharedLibraryCreateSuccess(p: { folderId: string; name: string }) {
    this.track('shared_library_create_success', p);
  }

  trackContentShareSuccess(p: {
    resourceId: string;
    resourceType: string;
    grant: string;
    shareWithDomain: boolean;
    recipientCount: number;
    isTopLevelSharedLibraryFolder: boolean;
  }) {
    this.track('content_share_success', p);
  }

  trackContentShareGrantUpdated(p: {
    resourceId: string;
    resourceType: string;
    grant: string;
    isTopLevelSharedLibraryFolder: boolean;
  }) {
    this.track('content_share_grant_updated', p);
  }

  trackContentShareAccessRemoved(p: {
    resourceId: string;
    resourceType: string;
    isTopLevelSharedLibraryFolder: boolean;
  }) {
    this.track('content_share_access_removed', p);
  }

  trackContentMovedToSharedLibrary(p: {
    sourceScope: string;
    targetScope: string;
    contentCount: number;
  }) {
    this.track('content_moved_to_shared_library', p);
  }

  trackLibraryCardActionClicked(p: {
    action:
      | 'share'
      | 'rename'
      | 'duplicate'
      | 'duplicate_and_move'
      | 'create_new_playlist'
      | 'add_to_playlist'
      | 'move'
      | 'delete';
    resourceType: string;
    resourceId: string;
    isFolder: boolean;
    libraryScope?: string;
  }) {
    this.track('library_card_action_clicked', {
      action: p.action,
      resource_type: p.resourceType,
      resource_id: p.resourceId,
      is_folder: p.isFolder,
      library_scope: p.libraryScope,
    });
  }

  trackLibraryMoveHereClicked(p: {
    sourceScope: string;
    targetScope: string;
    contentCount: number;
    targetFolderId: string | null;
  }) {
    this.track('library_move_here_clicked', {
      source_scope: p.sourceScope,
      target_scope: p.targetScope,
      content_count: p.contentCount,
      target_folder_id: p.targetFolderId,
    });
  }

  trackLibraryCopyHereClicked(p: {
    sourceScope: string;
    targetScope: string;
    contentCount: number;
    targetFolderId: string | null;
  }) {
    this.track('library_copy_here_clicked', {
      source_scope: p.sourceScope,
      target_scope: p.targetScope,
      content_count: p.contentCount,
      target_folder_id: p.targetFolderId,
    });
  }
}
